import React, { useEffect, useMemo, useState } from 'react';
import LeafletMap from '../../components/LeafletMap';
import { OrderList } from '../../components/StyledComponents';
import { Section } from '../../components/ui/Section';
import Store from '../../store/Store';

export interface SearchPageProps {
	id?: string;
	onChange?: (value: { startDate: string | null; endDate: string | null } | null) => void;
}

interface SearchOrder {
	resultAssets?: Array<{ uploadTime: string }>;
	[key: string]: unknown;
}

export default function SearchPage(props: SearchPageProps) {
	const searchOrders = Store.use('searchOrders') as SearchOrder[] | undefined;
	const [searchTimeError, setSearchTimeError] = useState<string | null>(null);
	const [dates, setDates] = useState<{ startDate: string | null; endDate: string | null }>({
		startDate: null,
		endDate: null,
	});
	const [filteredOrders, setFilteredOrders] = useState<SearchOrder[]>(searchOrders ?? []);

	const mapSetup = useMemo(() => {
		let timeout: ReturnType<typeof setTimeout> | undefined;
		return {
			onUpdateView: (view: { diag: number; center: { lat: number; lng: number } }) => {
				if (timeout != null) clearTimeout(timeout);
				timeout = setTimeout(() => {
					const dist = Math.min(Math.max(Math.floor(view.diag * 0.4), 100), 500000);
					Store.loadSearchWithin(view.center.lat, view.center.lng, dist);
				}, 1000);
			},
		};
	}, []);

	const handleValueChange = (
		newValue: { startDate: string | null; endDate: string | null } | null
	) => {
		setDates(newValue ?? { startDate: null, endDate: null });
		props.onChange?.(newValue ?? null);
	};

	useEffect(() => {
		const orders = searchOrders ?? [];
		if (dates.startDate && dates.endDate) {
			const startDate = new Date(dates.startDate);
			const endDate = new Date(dates.endDate);

			const filtered = orders.filter((order) =>
				(order.resultAssets ?? []).some((asset) => {
					const uploadTime = new Date(asset.uploadTime);
					return uploadTime >= startDate && uploadTime <= endDate;
				})
			);

			if (filtered.length === 0) {
				setSearchTimeError('Keine Drohnendaten in diesem Zeitraum gefunden');
			} else {
				setSearchTimeError(null);
			}
			setFilteredOrders(filtered);
		} else {
			setFilteredOrders(orders);
			setSearchTimeError(null);
		}
	}, [dates, searchOrders]);

	return (
		<div className="order-page">
			<div className="order-page-left">
				<Section>
					<h2>Nach öffentlichen Drohnendaten suchen</h2>
					<span>
						Geben Sie dazu ein Ziel ein oder scrollen Sie auf der Karte zum
						gewünschten Bereich.
					</span>
				</Section>

				<Section>
					<h3>Gefundene Aufträge in dem Bereich</h3>
					<OrderList
						interactive
						noPrice
						orders={filteredOrders as Parameters<typeof OrderList>[0]['orders']}
					/>
				</Section>
			</div>
			<div className="order-page-right">
				<LeafletMap
					setup={mapSetup}
					dateSearch
					geoSearch
					showOrders
					orders={filteredOrders as import('../../components/LeafletMap').LeafletMapProps['orders']}
					viewCache="extern_map"
					onChangeDates={(v: unknown) => handleValueChange(v as { startDate: string | null; endDate: string | null } | null)}
				/>
			</div>
		</div>
	);
}
